import axios from 'axios';
import type {
  SelfAxiosRequestConfig,
  SelfResponse,
  HttpResponse,
} from '@/types/request';
import { Message, Modal } from '@arco-design/web-vue';
import { useUserStore } from '@/store';
import { BASE_URL } from '@/utils/global';
import { version } from '@/config/app-version';

const instance: any = axios.create({
  baseURL: BASE_URL.webServer, // api的base_url
  timeout: 300000, // 请求超时时间
  withCredentials: true, // 允许携带cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// if (import.meta.env.VITE_API_BASE_URL) {
//   axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;
// }

instance.interceptors.request.use(
  (config: SelfAxiosRequestConfig) => {
    // let each request carry token
    // this example using the JWT token
    // Authorization is a custom headers key
    // please modify it according to the actual situation
    const excludeUrl = [`${BASE_URL.scmServer}/api/admin/account/login.ac`];

    const userStore = useUserStore();
    const { userLoginInfo } = userStore;
    if (config.url && !excludeUrl.includes(config.url) && userLoginInfo) {
      // 设置请求头token  scm-cid 和 scm-uid
      if (config.headers) {
        config.headers['scm-token'] = userLoginInfo.token;
        config.headers['scm-cid'] = userLoginInfo.cid;
        config.headers['scm-uid'] = userLoginInfo.id;
      }
    }
    if (config.headers) {
      config.headers['qs-referer'] = 'scmAppJson';
      config.headers['scm-pc-version'] = version;
    }
    return config;
  },
  (error: any) => {
    // do something
    return Promise.reject(error);
  }
);

// add response interceptors
instance.interceptors.response.use(
  async (response: SelfResponse<HttpResponse>) => {
    const res = response.data;
    // if the custom code is not 20000, it is judged as an error.
    if (
      res.status === 200 ||
      (res?.statusCode && [1, 2, 3, 5].includes(res.statusCode)) ||
      response.config.noErrorPop ||
      ['blob', 'arraybuffer'].includes(response.request?.responseType ?? '')
    ) {
      if (response.config.responseOriginData) {
        // amis返回response处理数据
        return response;
      }
      // is的statusCode情况待完善
      return res;
    }
    // 目前触发条件：在进销存修改了商品的颜色或尺码，生产开单的时候选中该商品同时勾选创建采购单
    if (res.status === 411) {
      Modal.info({
        title: '提示',
        hideCancel: true,
        content: res.message || '请求发生错误',
      });
    } else if (!response.config.noErrorToast) {
      // 低代码（设置了noErrorToast=true）的提示由amis自己提示，包括无权限
      Message.error({
        content: (res.message ?? res.content ?? '请求发生错误') as string,
        duration: 2 * 1000,
      });
    }
    if (res.status === 410) {
      const userStore = useUserStore();
      // 登录失效 todo
      await userStore.logout();
    }
    return Promise.reject(
      new Error((res.message ?? res.content ?? '请求发生错误') as string)
    );
  },
  async (error: any) => {
    const res = error.response?.data;
    if (error.config && error.config.errorSilence) {
      return Promise.reject(error);
    }
    if (error.response && error.response.status === 403) {
      // 如果服务端返回1403则认为当前请求资源要求登录，而当前会话未登录，跳至登录页面
      if (error.response.data && error.response.data.statusCode === 1403) {
        // 未登录
        const userStore = useUserStore();
        // 登录失效 todo
        await userStore.logout();
        // 不确定 app reload 会有什么问题
        // window.location.reload()
        return Promise.reject(error);
      }
      if (error.response.data && error.response.data.statusCode === 1401) {
        // 无权限
        // 跳转到无权限页面
        return Promise.reject(error);
      }
      if (error.response.data && error.response.data.content) {
        // 其他情况弹出错误提示
        // errorHandle(error);
      }
    } else if (
      !error.response?.config.noErrorToast ||
      error.response?.config.responseOriginData
    ) {
      // responseOriginData为true代表amis接口，接口报错时提示
      Message.error({
        content: (res?.message ?? res?.content ?? '请求发生错误') as string,
        duration: 2 * 1000,
      });
      // errorHandle(error);
    }
    return Promise.reject(error);
  }
);

export default instance;
